// This shares a lot of code with RunPagePanels, try to keep them
// in sync, or refactor
import React, {useState} from 'react';

import Inspector from '../../components/Inspector';
import {getTheme, Theme} from '../../pages/Benchmark/Theme';
import {PanelBankSectionsPagingContextProvider} from '../../state/panelbank/PanelBankSectionsPagingContext';
import * as ViewerHooks from '../../state/viewer/hooks';
import {usePart} from '../../state/views/hooks';
import {BenchmarkProject} from '../../types/graphql';
import {useRampFlagSemanticLegend} from '../../util/rampFeatureFlags';
import {useDetectPanelVariant} from '../Panel/hooks';
import {
  EmbeddablePanel,
  FullPagePanel,
  useStandalonePanelContext,
} from '../Panel/StandalonePanel';
import {AutoSyncPanelBankContextProvider} from '../PanelBank/AutoSyncPanelBankContext';
import {DefinedMetricContextProvider} from '../PanelBank/DefinedMetricContext';
import {PanelBank} from '../PanelBank/PanelBank';
import {PanelBankSectionsScrollContextProvider} from '../PanelBank/PanelBankSectionsScrollContextProvider';
import {PanelBankWidthContextProvider} from '../PanelBank/PanelBankWidthContext';
import {WandbConfigContextProvider} from '../PanelBank/WandbConfigContext';
import RunSelector from '../RunSelector';
import {PROJECT_READONLY_WORKSPACE_RUN_SELECTOR_DISABLED_FEATURES} from '../RunSelector/util';
import {PanelsSharedRefsContextProvider} from '../Views/contexts/PanelsSharedRefsContext';
import {NamedWorkspaceViewBarWrap} from '../Views/NamedWorkspaceViewBar';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {PanelsQuickAddContextProvider} from '../WorkspaceDrawer/PanelsQuickAdd/PanelsQuickAddContext';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendDefaults';
import {useSemanticLegendSettingsContext} from '../WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsContextProvider';
import {RunColorOptions} from '../WorkspaceDrawer/Settings/types';
import {useWorkspaceSettingsContext} from '../WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {WorkspaceDrawer} from '../WorkspaceDrawer/WorkspaceDrawer';
import {WorkspaceDrawerContextProvider} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {WorkspaceModePrompt} from '../WorkspaceModePrompt/WorkspaceModePrompt';
import * as S from './MultipleRunWorkspace.styles';
import {RunSelectorResizingContextProvider} from './RunSelectorResizingContext';
import {MultiRunWorkspaceAllProps} from './types';

export const MultiRunWorkspace: React.FC<MultiRunWorkspaceAllProps> = ({
  children,
  entityName,
  viewType,
  projectName,
  isTableExpanded,
  onSetTableExpanded,
  isSingleMode,
  onSelectRunName,
  selectedRunName,
  workspaceID,
  recommendedGrouping,
  showArtifactCounts,
  showLogCounts,
  title,
  project,
  // WorkspaceSelectors.MultiRunWorkspacePageData
  sectionPart,
  panelBankConfigRef,
  part,
  partRef,
  runSetRef,
}) => {
  const viewer = ViewerHooks.useViewer();
  const {isEmbeddedPanel, isFullScreenPanelPage} = useDetectPanelVariant();

  const isPanelPage = isEmbeddedPanel || isFullScreenPanelPage;
  const {isPanelNotFound} = useStandalonePanelContext();
  const hideRunSelector = (isPanelPage && isPanelNotFound) || isEmbeddedPanel;

  const linkedBenchmarkMeta: BenchmarkProject | null =
    !project.isBenchmark && project.linkedBenchmark != null
      ? project.linkedBenchmark
      : null;

  const linkedBenchmarkInfo: Theme | null =
    linkedBenchmarkMeta != null ? getTheme(linkedBenchmarkMeta) : null;

  // isProjectReadOnly refers to the current viewer's access to the project.
  // A user with readonly access to a project can make exploratory
  // workspace changes, e.g. search/filter runs, add/edit panels, etc.
  // This is much like playing around in a teammate's personal workspace,
  // where changes are simply discarded at the end of the session.
  // However, the user CANNOT create/edit/delete project-level resources
  // like runs, sweeps, or reports.
  const isProjectReadOnly = project.readOnly;

  const [panelBankWidth, setPanelBankWidth] = useState(0);

  const {
    workspaceLayout: {shouldAutoGeneratePanels},
  } = useWorkspaceSettingsContext();
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const workspaceSettings = usePart(workspaceSettingsRef);
  const {semanticLegendSettings} = useSemanticLegendSettingsContext();

  // If semantic legend is enabled and has been configured by the user, use the run color options from the semantic legend settings
  // Otherwise, use the original project colors.
  const runColorOptions =
    useRampFlagSemanticLegend(entityName) &&
    semanticLegendSettings?.metric !== DEFAULT_SEMANTIC_LEGEND_SETTINGS.metric // if the user hasn't configured the semantic legend, the panels would all be gray if we used the semantic legend settings
      ? workspaceSettings?.semanticLegendRunColorOptions
      : RunColorOptions.OriginalProjectColors;

  return (
    <PanelsSharedRefsContextProvider
      semanticLegendSettings={{
        ...semanticLegendSettings,
        runColorOptions: runColorOptions,
      }}
      customRunColorsRef={sectionPart.customRunColorsRef}
      panelBankConfigRef={panelBankConfigRef}
      runSetRefs={sectionPart.runSetRefs}
      workspaceSettingsRef={sectionPart.workspaceSettingsRef}>
      <WorkspaceDrawerContextProvider panelBankWidth={panelBankWidth}>
        <PanelBankSectionsPagingContextProvider>
          {!isEmbeddedPanel && (
            <NamedWorkspaceViewBarWrap
              workspaceStateId={workspaceID}
              viewType={viewType}
              isProjectReadOnly={isProjectReadOnly}
            />
          )}
          <S.MultipleRunWorkspace data-test="multiple-run-workspace">
            {shouldAutoGeneratePanels === 'pending' ? (
              <WorkspaceModePrompt />
            ) : (
              <RunSelectorResizingContextProvider>
                {!hideRunSelector && (
                  <RunSelector
                    benchmarkMeta={project.linkedBenchmark}
                    className="single-wb-table"
                    customRunColorsRef={sectionPart.customRunColorsRef}
                    disabledFeatures={
                      isProjectReadOnly
                        ? PROJECT_READONLY_WORKSPACE_RUN_SELECTOR_DISABLED_FEATURES
                        : undefined
                    }
                    displayBenchmark={linkedBenchmarkInfo != null}
                    isExpandable
                    isExpanded={isTableExpanded}
                    isSingleMode={isSingleMode}
                    // Make sure to remount if the runSetRef id changes (happens when we load a new workspace, or clear our workspace)
                    key={runSetRef.id}
                    // Unfortunately, `RunSelector` has a `readOnly` prop that means something completely different
                    // (it doesn't let the user set filters, grouping, etc.).
                    // Instead, this `readOnly` variable refers to the current viewer's access to the project
                    // (whether or not they can rename/delete runs, etc.), so we pass it in to `RunSelector` as the `noEdit` prop instead.
                    onSelectRunName={onSelectRunName}
                    onSetExpanded={onSetTableExpanded}
                    pageEntityName={entityName}
                    pageProjectName={projectName}
                    // TODO: We don't need this anymore right?
                    pollInterval={0}
                    recommendedGrouping={recommendedGrouping}
                    runQueues={project.runQueues}
                    runSetRef={runSetRef}
                    selectedRunName={selectedRunName}
                    showArtifactCounts={showArtifactCounts}
                    showLogCounts={showLogCounts}
                    title={title}
                  />
                )}
                <PanelsQuickAddContextProvider>
                  <PanelBankWidthContextProvider
                    panelBankWidth={panelBankWidth}
                    setPanelBankWidth={setPanelBankWidth}>
                    <WandbConfigContextProvider>
                      <DefinedMetricContextProvider>
                        <AutoSyncPanelBankContextProvider>
                          <PanelBankSectionsScrollContextProvider>
                            {isEmbeddedPanel ? (
                              <EmbeddablePanel
                                isProjectReadOnly={isProjectReadOnly}
                              />
                            ) : isFullScreenPanelPage ? (
                              <FullPagePanel
                                isProjectReadOnly={isProjectReadOnly}
                              />
                            ) : children ? (
                              <div style={{flexGrow: 1, overflow: 'auto'}}>
                                {children}
                              </div>
                            ) : (
                              !isTableExpanded && (
                                <PanelBank
                                  entityName={entityName}
                                  projectName={projectName}
                                  isProjectReadOnly={isProjectReadOnly}
                                  workspaceID={workspaceID}
                                />
                              )
                            )}
                            {viewer?.userInfo?.bio?.includes('INSPECTOR') && (
                              <Inspector />
                            )}
                            <WorkspaceDrawer />
                          </PanelBankSectionsScrollContextProvider>
                        </AutoSyncPanelBankContextProvider>
                      </DefinedMetricContextProvider>
                    </WandbConfigContextProvider>
                  </PanelBankWidthContextProvider>
                </PanelsQuickAddContextProvider>
              </RunSelectorResizingContextProvider>
            )}
          </S.MultipleRunWorkspace>
        </PanelBankSectionsPagingContextProvider>
      </WorkspaceDrawerContextProvider>
    </PanelsSharedRefsContextProvider>
  );
};
